import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Home, TrendingUp, Percent, AlertTriangle, Hammer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function RealEstateWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics/real-estate").then((r) => setData(r.data)).catch(() => setData({ available: false }));
  }, []);

  if (!data || !data.available) return null;
  const currency = data.base_currency || user?.base_currency || data.currency || "USD";
  const avgYield = Number(data.avg_yield ?? 0);
  const avgNetYield = Number(data.avg_net_yield ?? 0);
  const alerts = Number(data.alerts_count ?? 0);
  const withMortgage = (data.items || []).filter((it) => it.mortgage_id);

  return (
    <div className="card-surface p-6" data-testid="real-estate-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#1C7C54]/10 flex items-center justify-center">
          <Home className="w-4 h-4 text-[#1C7C54]" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Inmobiliario</p>
          <h3 className="font-display text-lg font-semibold text-[#0A110E]">
            {data.count} {data.count === 1 ? "propiedad" : "propiedades"}
          </h3>
        </div>
        {alerts > 0 && (
          <div
            data-testid="revaluation-alerts-badge"
            className="flex items-center gap-1 rounded-full bg-[#D95D39]/10 text-[#D95D39] px-2 py-1 text-[10px] font-semibold uppercase tracking-wider"
            title={`${alerts} propiedad(es) con revalorización > ${data.revaluation_threshold}%`}
          >
            <AlertTriangle className="w-3 h-3" />
            {alerts} alerta{alerts === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Valor total</div>
          <div className="font-display text-lg font-semibold tabular-nums text-[#0A110E]">
            {formatCurrency(data.total_value, currency)}
          </div>
        </div>
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Equity neto</div>
          <div className="font-display text-lg font-semibold tabular-nums text-[#1C7C54]">
            {formatCurrency(data.total_equity, currency)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <div className="rounded-lg bg-[#FAFAF9] px-2 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#8A9992]">Renta / mes</div>
          <div className="text-sm font-semibold tabular-nums text-[#0A110E] mt-0.5">
            {formatCurrency(data.total_rental_monthly, currency)}
          </div>
        </div>
        <div className="rounded-lg bg-[#FAFAF9] px-2 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#8A9992] flex items-center justify-center gap-1">
            <Percent className="w-2.5 h-2.5" /> Yield bruto/neto
          </div>
          <div className="text-sm font-semibold tabular-nums text-[#1C7C54] mt-0.5">
            {avgYield.toFixed(2)}%
            <span className="text-[#8A9992] font-normal"> · </span>
            <span className={avgNetYield >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"}>
              {avgNetYield.toFixed(2)}%
            </span>
          </div>
        </div>
        <div className="rounded-lg bg-[#FAFAF9] px-2 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#8A9992] flex items-center justify-center gap-1">
            <TrendingUp className="w-2.5 h-2.5" /> % patrimonio
          </div>
          <div className="text-sm font-semibold tabular-nums text-[#0A110E] mt-0.5">
            {data.pct_of_net_worth}%
          </div>
        </div>
      </div>

      {Number(data.total_capex || 0) > 0 && (
        <div
          data-testid="real-estate-capex"
          className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-[#C89A3F]/25 bg-[#C89A3F]/5 px-3 py-2"
        >
          <div className="flex items-center gap-2 text-[#8B6B1F]">
            <Hammer className="w-3.5 h-3.5" />
            <span className="text-[10px] uppercase tracking-[0.1em] font-bold">Reformas / capex</span>
          </div>
          <div className="font-display text-base font-semibold tabular-nums text-[#8B6B1F]">
            {formatCurrency(data.total_capex, currency)}
          </div>
        </div>
      )}

      {withMortgage.length > 0 && (
        <div className="pt-3 border-t border-[#EBEBEA] space-y-2" data-testid="mortgage-progress-section">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">
            Amortización de hipotecas
          </div>
          {withMortgage.slice(0, 4).map((it) => {
            const pct = Math.max(0, Math.min(100, Number(it.mortgage_paid_pct) || 0));
            const ltv = Number(it.ltv_pct) || 0;
            let ltvCls = "bg-[#1C7C54]/10 text-[#1C7C54]";
            if (ltv > 80) ltvCls = "bg-[#D95D39]/10 text-[#D95D39]";
            else if (ltv >= 60) ltvCls = "bg-[#C89A3F]/10 text-[#8B6B1F]";
            return (
              <div key={it.id} data-testid={`mortgage-progress-${it.id}`} className="space-y-1">
                <div className="flex items-center justify-between text-xs gap-2">
                  <span className="text-[#0A110E] truncate flex-1">{it.name}</span>
                  {ltv > 0 && (
                    <span
                      data-testid={`widget-ltv-${it.id}`}
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full tabular-nums ${ltvCls}`}
                    >
                      LTV {ltv}%
                    </span>
                  )}
                  <span className="tabular-nums text-[#5F7368]">
                    {pct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-[#EBEBEA] overflow-hidden">
                  <div
                    className="h-full bg-[#1C7C54] transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
          {withMortgage.length > 4 && (
            <div className="text-[10px] text-[#8A9992] text-right">
              +{withMortgage.length - 4} más
            </div>
          )}
        </div>
      )}
    </div>
  );
}
